// Description: Component that render the user's campaign data into a graphic
// Updated: 02/07/2025

// React
import React, { useEffect, useRef, useState } from 'react';

// Material UI Components
import { Box, CircularProgress, Skeleton, useMediaQuery } from '@mui/material';

// Components and functions to the project
import axios from 'axios';
import Chart from 'chart.js/auto';
import ThemeColors from '../../components/ThemeColors/ThemeColors';
import ModeGraphicModal from './ModeGraphicModal';
import use123zapStore from '../../context/123zapStore';
import Chip from '@mui/material/Chip';

// Tooltip
const customTooltip = (dataGraphic) => (context) => {

    console.log('Custom Tooltip Context:', context);

    let tooltipEl = document.getElementById('chartjs-tooltip');

    // Cria o elemento de tooltip se não existir
    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = 'chartjs-tooltip';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.zIndex = 1000;  // Para garantir que o tooltip apareça acima de outros elementos
        document.body.appendChild(tooltipEl);
    }

    const tooltipModel = context.tooltip;

    if (tooltipModel.opacity === 0) {
        tooltipEl.classList.remove('show');
        return;
    }

    tooltipEl.classList.add('show');
    tooltipEl.classList.remove('above', 'below', 'no-transform');
    if (tooltipModel.yAlign) {
        tooltipEl.classList.add(tooltipModel.yAlign);
    } else {
        tooltipEl.classList.add('no-transform');
    }

    const label = tooltipModel.dataPoints[0].label;

    const dataForPoint = dataGraphic.filter(item => item.month_year === label);
    const grouped = dataForPoint.reduce((acc, item) => {
        const statusKey = item.final_status.replace('total_', '');
        acc[statusKey] = (acc[statusKey] || 0) + parseInt(item.status_count || 0);
        return acc;
    }, {});

    // adiciona ao grouped o total geral com o valor total de todos os status

    grouped.total_geral = Object.values(grouped).reduce((sum, val) => sum + val, 0);

    const statusMap = {
        "delivered": { label: "Entregue", color: "#25D366" }, // Verde
        "failed": { label: "Falhou", color: "#E53935" },    // Vermelho
        "not_confirmed": { label: "Não Confirmado", color: "#757575" },  // Laranja
        "read": { label: "Lido", color: "#4FC3F7" },        // Azul
        "received": { label: "Recebido", color: "#9C27B0" }, // Roxo
        "unknown": { label: "Desconhecido", color: "#9E9E9E" }, // Cinza
        "waiting": { label: "Aguardando", color: "#FFC107" }, // Amarelo
        "total_geral": { label: "Total Geral", color: "#673AB7" } // Roxo Escuro
    };

    const totalGeral = Object.values(grouped).reduce((sum, val) => sum + val, 0);

    // preço do valor do eixo x clicado

    const eixoX = tooltipModel.dataPoints[0].label;

    function formatNumberWithSpaces(num) {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    }

    const htmlLines = [
        `<div style="font-size: 15px; margin-left: 15px; margin-bottom: 15px; text-align: left;">${eixoX}</div>`,

        `<hr style="width: 100%; border: none; border-top: 1px solid #ddd">`,

        ...Object.entries(grouped).map(([status, value]) => {
            const statusInfo = statusMap[status] || { label: status, color: "#000" };
            const formattedValue = formatNumberWithSpaces(value);
            return `
            <div style="display: flex; align-items: center; margin: 15px; font-size: 14px;">
                <div style="display: flex; align-items: center; flex-grow: 1; min-width: 0;">
                    <div style="
                        width: 15px; 
                        height: 15px; 
                        background-color: ${statusInfo.color}; 
                        border-radius: 50%; 
                        margin-right: 15px; 
                        border: 2px solid white; 
                        box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
                    "></div>
                    <span>${statusInfo.label}</span>
                </div>
                <div style="width: 50px; text-align: left; padding-left: 20px;">
                    <strong style="color: #333;">${formattedValue}</strong>
                </div>
            </div>
        `;
        }),
    ];

    // ...

    tooltipEl.innerHTML = htmlLines.join('');

    const canvas = context.chart.canvas;
    const position = canvas.getBoundingClientRect();

    tooltipEl.style.position = 'absolute';

    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;

    const pageX = position.left + window.pageXOffset + tooltipModel.caretX;
    const pageY = position.top + window.pageYOffset + tooltipModel.caretY - tooltipHeight;

    let left = pageX;
    let top = pageY;

    const viewportRight = window.pageXOffset + window.innerWidth;

    if (left + tooltipWidth > viewportRight) {
        left = pageX - tooltipWidth - 20;
        if (left < window.pageXOffset) {
            left = window.pageXOffset + 5;
        }
    }

    tooltipEl.style.left = left + 'px';
    tooltipEl.style.top = top + 'px';

    tooltipEl.style.font = tooltipModel.options.bodyFont.string;
    tooltipEl.style.padding = tooltipModel.options.padding + 'px ' + tooltipModel.options.padding + 'px';
    tooltipEl.style.pointerEvents = 'none';

};

// Render The Component
const ModeGraphic = () => {

    const setTableColumns = use123zapStore((state) => state.setTableColumns);
    const setCampaignsSummaryData = use123zapStore((state) => state.setCampaignsSummaryData);
    const setExcelTableColumns = use123zapStore((state) => state.setExcelTableColumns);
    const setExcelTableRows = use123zapStore((state) => state.setExcelTableRows);
    const setShowNewTable = use123zapStore((state) => state.setShowNewTable);
    const setShowReceived = use123zapStore((state) => state.setShowReceived);
    const setReceivedMessages = use123zapStore((state) => state.setReceivedMessages);
    const modalData = use123zapStore((state) => state.modalData);
    const setModalData = use123zapStore((state) => state.setModalData);

    const resetModal = () => {
        setShowNewTable(false);
        setExcelTableColumns([]);
        setExcelTableRows([]);
        setTableColumns([]);
        setCampaignsSummaryData([]);
        setShowReceived(false);
        setReceivedMessages({
            columns: [],
            rows: []
        });
    };

    const lastClickedDateRef = useRef(null);
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const [dataGraphic, setDataGraphic] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingTable, setLoadingTable] = useState(false);

    const hexToRgb = (hex) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, 0.2)`;
    };

    const isSmallScreen = useMediaQuery("(max-width: 600px)");

    useEffect(() => {
        const authorization = localStorage.getItem('jwt_token');
        const client_id = localStorage.getItem('idToken');
        const url = process.env.REACT_APP_BACKEND_AWS_URL;

        function getLastSixMonthsPeriod() {
            const today = new Date();

            const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);

            const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 5, 1);

            const formatDate = (date) => {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            };

            return {
                start_date: formatDate(sixMonthsAgo),
                end_date: formatDate(endDate)
            };
        }

        const { start_date, end_date } = getLastSixMonthsPeriod();

        axios
            .post(`${url}/dashboard/messages/sent/monthly`, {
                start_date,
                end_date,
                "refresh": true
            }, {
                headers: { Authorization: authorization, "Client_ID": client_id },
            })
            .then((response) => {
                setDataGraphic(response.data.data);
            })
            .catch((error) => {
                console.error('Erro ao buscar os dados:', error);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!chartRef.current) return;

        const ctx = chartRef.current.getContext('2d');
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }

        const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];

        const fakeDataset = {
            labels,
            datasets: [
                {
                    label: 'Carregando...',
                    data: [20, 35, 25, 40, 30, 45],
                    borderColor: '#e0e0e0',
                    backgroundColor: '#f5f5f5',
                    borderDash: [5, 5],
                    tension: 0.4,
                    fill: true,
                    pointRadius: 0,
                },
            ],
        };

        const labelsReal = [...new Set(dataGraphic.map((item) => item.month_year))].sort();

        const totalsByMonth = labelsReal.map((month) => {
            return dataGraphic
                .filter((item) => item.month_year === month && item.final_status !== 'total_unknown')
                .reduce((acc, item) => acc + (parseInt(item.status_count) || 0), 0);
        });

        const gradient = ctx.createLinearGradient(0, 0, 0, chartRef.current.height);
        gradient.addColorStop(0, hexToRgb(ThemeColors.purple700));
        gradient.addColorStop(1, hexToRgb(ThemeColors.purple500));

        const chartData =
            loading || dataGraphic.length === 0
                ? fakeDataset
                : {
                    labels: labelsReal,
                    datasets: [
                        {
                            label: 'Total Geral',
                            data: totalsByMonth,
                            borderColor: 'blue',
                            backgroundColor: gradient,
                            tension: 0.3,
                            fill: true,
                            pointRadius: 6.5,
                        },
                    ],
                };



        chartInstanceRef.current = new Chart(ctx, {
            type: 'line',
            data: chartData,
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        stacked: true,
                        grid: {
                            drawOnChartArea: true,
                            borderDash: [5, 5],
                            display: false
                        },
                        ticks: {
                            font: {
                                family: 'Satoshi',
                                size: 14
                            },
                        },
                        display: true,
                        position: 'bottom',
                    },
                    y: {
                        stacked: false,
                        beginAtZero: true,
                        suggestedMin: 0,
                        suggestedMax: 10,
                        grid: {
                            borderDash: [5, 5],
                            display: true
                        },
                        ticks: {
                            display: true,
                        },
                        display: true,
                        position: 'transparent',
                    }
                },
                onClick: (event) => {
                    const activePoints = chartInstanceRef.current.getElementsAtEventForMode(
                        event,
                        'nearest',
                        { intersect: true },
                        false
                    );

                    if (activePoints.length > 0) {
                        const dataIndex = activePoints[0].index;
                        const date = chartInstanceRef.current.data.labels[dataIndex];

                        const isSameDate = lastClickedDateRef.current === date;

                        if (!isSameDate) {
                            resetModal();
                            setModalData({ label: '', date });
                            lastClickedDateRef.current = date;
                            setLoadingTable(true);
                        }

                        setOpenModal(true);

                    }
                },
                onHover: (event, chartElement) => {
                    const target = event.native ? event.native.target : event.target;
                    if (chartElement.length > 0) {
                        target.style.cursor = 'pointer';
                    } else {
                        target.style.cursor = 'default';
                    }
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        enabled: false,
                        external: customTooltip(dataGraphic),
                    },
                },
                scales: {
                    x: { display: true },
                    y: { display: true, beginAtZero: true },
                },
            },
        });
    }, [dataGraphic]);

    return (
        <>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Chip
                    label={
                        loading
                            ? 'Estamos carregando os dados...'
                            : dataGraphic.length === 0
                                ? 'Parece que você ainda não enviou mensagens.'
                                : 'Relação de mensagens enviadas'
                    }
                    color={loading ? 'default' : dataGraphic.length === 0 ? 'warning' : 'default'}
                    variant="outlined"
                    sx={{
                        fontWeight: 500,
                        ...(!loading && dataGraphic.length > 0 && {
                            color: '#8033F4',
                            borderColor: '#8033F4',
                        }),
                        ...(loading && {
                            color: 'gray',
                            borderColor: 'gray',
                        }),
                    }}
                />
            </Box>
            <Box sx={{ height: '400px', position: 'relative', marginTop: '40px', margin: '20px' }}>
                <canvas style={{ overflow: 'inherit' }} ref={chartRef}></canvas>
            </Box>
            <ModeGraphicModal open={openModal} onClose={() => setOpenModal(false)} data={modalData} loading={loadingTable} setLoading={setLoadingTable} />
        </>
    );
};

export default ModeGraphic;